import React, {Fragment} from 'react';
import {DataTablePage} from '@common/datatable/page/data-table-page';
import {Trans} from '@ui/i18n/trans';
import {DeleteSelectedItemsAction} from '@common/datatable/page/delete-selected-items-action';
import {DataTableEmptyStateMessage} from '@common/datatable/page/data-table-emty-state-message';

import todoImage from '@app/admin/lists/todo.svg';
import {DataTableAddItemButton} from '@common/datatable/data-table-add-item-button';
import {ColumnConfig} from '@common/datatable/column-config';
import {FormattedDate} from '@ui/i18n/formatted-date';
import {EditIcon} from '@ui/icons/material/Edit';
import {Tooltip} from '@ui/tooltip/tooltip';
import {IconButton} from '@ui/buttons/icon-button';
import {HostAcc} from '@app/titles/models/HostAcc';
import {FormattedRelativeTime} from '@ui/i18n/formatted-relative-time';
import {DialogTrigger} from '@ui/overlays/dialog/dialog-trigger';
import {DebridorsAddAccount} from '@common/auth/ui/account-settings/debridors-add-account';
import {ChipList} from '@ui/forms/input-field/chip-field/chip-list';
import {Chip} from '@ui/forms/input-field/chip-field/chip';
import {EnumDebridors} from '@ui/i18n/enum-debridors';
import {InfoDialogTrigger} from '@ui/overlays/dialog/info-dialog-trigger/info-dialog-trigger';
import {ChannelsDocsLink} from '@common/admin/channels/channels-docs-link';

export function DebridorsDatatablePageAdmin() {
  const actions: ColumnConfig<HostAcc>[] = [
    {
      key: 'actions',
      header: () => <Trans message="Actions" />,
      hideHeader: true,
      visibleInMode: 'all',
      align: 'end',
      width: 'w-84 flex-shrink-0',
      body: acc => (
        <Fragment>
          <DialogTrigger type="modal">
            <Tooltip label={<Trans message="Edit" />}>
              <IconButton size="md">
                <EditIcon />
              </IconButton>
            </Tooltip>
            <DebridorsAddAccount account={acc} />
          </DialogTrigger>
        </Fragment>
      ),
    },
  ];
  const commonColumns: ColumnConfig<HostAcc>[] = [
    {
      key: 'username',
      width: 'flex-3',
      allowsSorting: true,
      visibleInMode: 'all',
      header: () => <Trans message="Username" />,
      body: acc => acc.username,
    },
    {
      key: 'premium',
      header: () => <Trans message="Account Status" />,
      body: acc => (
        <ChipList
          radius="rounded"
          color={
            acc.expire_at && new Date(acc.expire_at) > new Date()
              ? 'positive'
              : 'chip'
          }
          size="xs"
        >
          <Chip selectable>
            <Trans
              message={
                acc.expire_at && new Date(acc.expire_at) > new Date()
                  ? 'Premium'
                  : 'Free'
              }
            />
          </Chip>
        </ChipList>
      ),
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'host_id',
      header: () => <Trans message="Type" />,
      body: acc => <EnumDebridors {...acc} />,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'expire_at',
      header: () => <Trans message="Expiration" />,
      body: acc => <FormattedDate preset="short" date={acc.expire_at} />,
      width: 'w-124 flex-shrink-0',
    },
    {
      key: 'last_check',
      allowsSorting: true,
      width: 'w-124 flex-shrink-0',
      header: () => <Trans message="Last check" />,
      body: acc =>
        acc.last_check ? <FormattedRelativeTime date={acc.last_check} /> : '',
    },

    {
      key: 'active',
      header: () => <Trans message="Status" />,
      body: acc => <Trans message={acc.active ? 'Active' : 'Inactive'} />,
    },
  ];

  const HostListDatatableColumns: ColumnConfig<HostAcc>[] = [
    ...commonColumns,
    ...actions,
  ];

  return (
    <DataTablePage
      endpoint={'debridors'}
      title={<Trans message="Debridors Accounts" />}
      columns={HostListDatatableColumns}
      actions={<Actions />}
      headerContent={<InfoTrigger />}
      selectedActions={<DeleteSelectedItemsAction />}
      emptyStateMessage={
        <DataTableEmptyStateMessage
          image={todoImage}
          title={<Trans message="No accounts added yet" />}
          filteringTitle={<Trans message="No matching accounts found" />}
        />
      }
    />
  );
}
function InfoTrigger() {
  return (
    <InfoDialogTrigger
      body={
        <Fragment>
          <Trans message="Here you can add and manage your debridors accounts." />
          <ChannelsDocsLink className="mt-14" />
        </Fragment>
      }
    />
  );
}
function Actions() {
  return (
    <Fragment>
      <DialogTrigger type="modal">
        <DataTableAddItemButton>
          <Trans message="Add new account" />
        </DataTableAddItemButton>
        <DebridorsAddAccount />
      </DialogTrigger>
    </Fragment>
  );
}
